import {
  dashboard20240730Client,
  DashboardApi20240730
} from '@sequencehq/api/dashboard/v20240730'
import {
  InfoBox,
  InfoRow,
  Link,
  TextInputField,
  Button,
  Spinner,
  useNotifications,
  DateRangePickerField,
  Box,
  Flex
} from '@sequencehq/core-components'
import {
  GreyGrey50,
  GreyGrey70,
  IndigoIndigo60,
  Lato12Regular,
  Primary13Regular,
  Primary14ExtendedSemiBold
} from '@sequencehq/core-components'
import {
  format,
  formatDateRange,
  startOfMonth,
  endOfMonth,
  sub
} from '@sequencehq/utils/dates'
import { useForm } from '@sequencehq/utils'
import { required, validateParameter } from '@sequencehq/utils/validation'
import { CustomerSelect } from 'Customer/index'
import { noReturn } from 'lib/noReturn'
import { FC, useState } from 'react'
import { useLogEvent } from 'lib/postHog/useLogEvent'
import { match } from 'ts-pattern'

type UsageMetricPreviewProps = {
  usageMetricId: string
  aggregationType: DashboardApi20240730.UsageMetrics.AggregationType
  parameters: DashboardApi20240730.UsageMetrics.UsageMetricParameter[]
}

export const toAggregationType = (
  aggregationType: DashboardApi20240730.UsageMetrics.AggregationType
): string => {
  return match(aggregationType)
    .with('COUNT', () => 'Count')
    .with('UNIQUE', () => 'Unique')
    .with('SUM', () => 'Sum')
    .with('CUSTOM', () => 'Result')
    .exhaustive()
}

export const UsageMetricPreview: FC<UsageMetricPreviewProps> = ({
  usageMetricId,
  aggregationType,
  parameters
}) => {
  const { displayNotification } = useNotifications()
  const { logEvent } = useLogEvent()
  const [calculationResult, setCalculationResult] =
    useState<DashboardApi20240730.GetCalculateUsageMetric.CalculationResult | null>(
      null
    )
  const [isCalculating, setIsCalculating] = useState(false)

  const parameterDefaultValues: Record<string, string> = {}
  parameters.forEach(parameter => {
    parameterDefaultValues[parameter.id] = parameter.defaultValue
  })

  const parameterFieldConfigs = parameters.map(parameter => ({
    property: `customParameters.${parameter.id}` as const,
    validation: [required, validateParameter(parameter.type)]
  }))

  const [showValidationErrors, setShowValidationErrors] = useState(false)
  const { fields, queries } = useForm<{
    customerId: string
    dateRange?: [Date, Date]
    customParameters: Record<string, string>
  }>({
    value: {
      customerId: '',
      dateRange: undefined as [Date, Date] | undefined,
      customParameters: parameterDefaultValues
    },
    fieldConfiguration: [
      {
        property: 'customerId',
        validation: [required]
      },
      {
        property: 'dateRange',
        validation: [required]
      },
      ...parameterFieldConfigs
    ],
    showValidationErrors,
    onChange: () => {}
  })

  const setCurrentMonth = () => {
    const now = new Date()
    const startOfCurrentMonth = startOfMonth(now)
    const endOfCurrentMonth = endOfMonth(now)

    fields.dateRange.onChange([startOfCurrentMonth, endOfCurrentMonth])
  }

  const setPreviousMonth = () => {
    const now = new Date()
    const previousMonth = sub(now, { months: 1 })
    const startOfPreviousMonth = startOfMonth(previousMonth)
    const endOfPreviousMonth = endOfMonth(previousMonth)

    fields.dateRange.onChange([startOfPreviousMonth, endOfPreviousMonth])
  }

  const handleSubmit = async () => {
    if (!queries.isValid || !queries.formData.dateRange) {
      setShowValidationErrors(true)
      return
    }

    try {
      setIsCalculating(true)
      const aliasRes = await dashboard20240730Client.getAliasesForCustomer({
        customerId: queries.formData.customerId
      })

      const aliases = aliasRes.data?.items.map(({ value }) => value) ?? []

      const calcRes = await dashboard20240730Client.getCalculateUsageMetric({
        id: usageMetricId,
        customerAliases: [queries.formData.customerId, ...aliases],
        periodStart: format(
          queries.formData.dateRange[0],
          "yyyy-MM-dd'T'HH:mm:ss'Z'"
        ),
        periodEnd: format(
          queries.formData.dateRange[1],
          "yyyy-MM-dd'T'HH:mm:ss'Z'"
        ),
        customParameters: queries.formData.customParameters
      })
      setCalculationResult(calcRes.data)

      if (!calcRes.data) {
        displayNotification('An error occurred calculating the preview', {
          type: 'error'
        })
      }
    } catch {
      displayNotification('An error occurred calculating the preview', {
        type: 'error'
      })
    } finally {
      setIsCalculating(false)
    }
  }

  const renderContent = () => {
    if (isCalculating) {
      return (
        <Flex flex="1" justifyContent="center" alignItems="center">
          <Spinner color={GreyGrey70} />
        </Flex>
      )
    }

    if (calculationResult) {
      return (
        <>
          {aggregationType !== 'CUSTOM' && (
            <InfoRow label="Event count">
              {calculationResult.eventCount}
            </InfoRow>
          )}
          <Box height={2} />

          <InfoRow
            label={`${toAggregationType(calculationResult.aggregationType)}:`}
          >
            {calculationResult.value}
          </InfoRow>

          <Box height={2} />
          {queries.formData.dateRange && (
            <InfoRow
              label={formatDateRange({
                from: queries.formData.dateRange[0],
                to: queries.formData.dateRange[1]
              })}
            />
          )}
        </>
      )
    }

    return (
      <Flex flex="1" justifyContent="center" alignItems="center">
        <Box {...Primary13Regular} color={GreyGrey50} textAlign="center">
          Select a customer and date range to calculate usage
        </Box>
      </Flex>
    )
  }

  return (
    <Box>
      <Box {...Primary14ExtendedSemiBold} marginBottom="8px">
        Preview
      </Box>
      <Flex flexDirection="column" gap="16px">
        <InfoBox>
          <Flex
            flexDirection="column"
            minHeight="96px"
            height="100%"
            data-testid="usageMetric.calculationPreview"
          >
            {renderContent()}
          </Flex>
        </InfoBox>
        <Flex
          flexDirection="column"
          gap="16px"
          onSubmit={noReturn(handleSubmit)}
        >
          <CustomerSelect
            label="Customer"
            value={fields.customerId.value}
            onChange={fields.customerId.onChange}
            validationErrors={fields.customerId.validationErrors}
            disabled={fields.customerId.disabled}
            allowCreate={false}
          />
          <Box>
            <DateRangePickerField
              value={fields.dateRange.value}
              onChange={fields.dateRange.onChange}
              placeholder="Select date range"
              data-testid="usageMetric.calculationPreview.dateRange"
              withOptions={false}
              validationErrors={fields.dateRange.validationErrors}
              label="Date range"
              inputWidth="100%"
            />
            <Flex gap={2} mt={1} {...Lato12Regular}>
              <Link
                color={IndigoIndigo60}
                cursor="pointer"
                onClick={e => {
                  e.preventDefault()
                  setPreviousMonth()
                }}
              >
                Previous month
              </Link>
              <Box color={GreyGrey50}>|</Box>
              <Link
                color={IndigoIndigo60}
                data-testid="usageMetric.calculationPreview.currentMonth"
                cursor="pointer"
                onClick={e => {
                  e.preventDefault()
                  setCurrentMonth()
                }}
              >
                Current month
              </Link>
            </Flex>
          </Box>
          {parameters.map(parameter => {
            const fieldKey =
              `customParameters.${parameter.id}` as keyof typeof fields
            const field = fields[fieldKey]

            return (
              <TextInputField
                key={parameter.id}
                data-testid={`usageMetric.calculationPreview.parameter.${parameter.id}`}
                label={parameter.name}
                value={field.value as string}
                onChange={e =>
                  (field.onChange as (value: string) => void)(e.target.value)
                }
                validationErrors={field.validationErrors}
                disabled={field.disabled}
                infoPopover={{
                  body: parameter.description
                }}
              />
            )
          })}

          <Flex justifyContent="flex-end">
            <Button
              variant="primary"
              data-testid="usageMetric.calculationPreview.calculate"
              onClick={() => {
                logEvent({
                  eventName: 'usage_metric_preview_calculate_clicked'
                })
                handleSubmit()
              }}
            >
              Calculate
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </Box>
  )
}
